import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Stack,
    Typography,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Box,
    Avatar,
    Chip,
} from '@mui/material';
import {
    Add as AddIcon,
    Remove as RemoveIcon,
    Sync as ChangeIcon,
    PublishedWithChanges as ReplacedIcon,
} from '@mui/icons-material';
import { useMemo } from 'react';
import { useI18nContext } from '@/i18n/i18n-react';
import type { EventAuditEntry } from '@/types';
import { useGetEventAuditQuery } from '@/store/apiSlice';
import { useCategoriesResources } from '@/store/hooks';

/**
 * EventAuditModal
 *
 * Displays audit history for ALL categories in an event (by date) in a single modal.
 * This is more efficient than PlanResourcesAuditModal when you want to see the complete
 * audit trail for an entire event, as it makes only one API request instead of multiple
 * requests per category.
 *
 * Use this when:
 * - Showing audit history for an entire event
 * - Admin wants to see all changes across categories
 *
 * Use PlanResourcesAuditModal when:
 * - Showing audit history for a specific category only
 */
export interface EventAuditModalProps {
    open: boolean;
    onClose: () => void;
    date: string;
    eventTitle?: string;
    categoryId?: number; // Optional: filter to show only this category
    categoryName?: string; // Optional: category name for title
}

const EventAuditModal = ({
    open,
    onClose,
    date,
    eventTitle,
    categoryId,
    categoryName,
}: EventAuditModalProps) => {
    const { LL } = useI18nContext();

    const formatTime = (iso: string) => {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        // Compact datetime display
        return d.toLocaleString(undefined, {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getActionAvatar = (action: string) => {
        const sxBase = { width: 28, height: 28, fontSize: '0.75rem' };

        switch (action) {
            case 'assign':
                return (
                    <Avatar
                        sx={{
                            ...sxBase,
                            bgcolor: 'success.main',
                            color: 'common.white',
                        }}
                        title={action}
                    >
                        <AddIcon fontSize="small" />
                    </Avatar>
                );
            case 'unassign':
                return (
                    <Avatar
                        sx={{
                            ...sxBase,
                            bgcolor: 'error.main',
                            color: 'common.white',
                        }}
                        title={action}
                    >
                        <RemoveIcon fontSize="small" />
                    </Avatar>
                );
            case 'replace':
                return (
                    <Avatar
                        sx={{
                            ...sxBase,
                            bgcolor: 'info.main',
                            color: 'common.white',
                        }}
                        title={action}
                    >
                        <ReplacedIcon fontSize="small" />
                    </Avatar>
                );
            case 'change-request-created':
                return (
                    <Avatar
                        sx={{
                            ...sxBase,
                            bgcolor: 'warning.main',
                            color: 'common.white',
                        }}
                        title={action}
                    >
                        <ChangeIcon fontSize="small" />
                    </Avatar>
                );
            case 'change-request-cancelled':
                return (
                    <Avatar
                        sx={{
                            ...sxBase,
                            bgcolor: 'warning.main',
                            color: 'common.white',
                        }}
                        title={action}
                    >
                        <ChangeIcon fontSize="small" />
                    </Avatar>
                );
            default:
                // Skip entries with empty or invalid actions
                return null;
        }
    };

    const getOldAssignedFromMeta = (meta: unknown): number | null => {
        if (!meta || typeof meta !== 'object') return null;
        const anyMeta = meta as any;
        const v = anyMeta.oldAssignedResourceId;
        return typeof v === 'number' ? v : v ? Number(v) : null;
    };

    // Map resource IDs -> names from categories cache to resolve missing names
    const categories = useCategoriesResources();

    const resourceNameById = useMemo(() => {
        const m = new Map<number, string>();
        categories.forEach((cat) => {
            (cat.resources || []).forEach((r: any) => {
                if (r && typeof r.id === 'number' && r.name) m.set(r.id, r.name);
            });
        });
        return m;
    }, [categories]);

    const getAssignedLabel = (row: EventAuditEntry) =>
        row.assignedResourceName ??
        (row.assignedResourceId && resourceNameById.get(row.assignedResourceId)) ??
        (row.assignedResourceId ? `#${row.assignedResourceId}` : '—');

    const getActorLabel = (row: EventAuditEntry) =>
        row.actorResourceName ??
        (row.actorResourceId && resourceNameById.get(row.actorResourceId)) ??
        (row.actorResourceId ? `#${row.actorResourceId}` : '—');

    const { data, isLoading, isError, error } = useGetEventAuditQuery(
        { date },
        { skip: !open || !date }
    );

    // Filter out entries with empty actions
    const validData = useMemo(() => {
        if (!data) return [];
        return data.filter((row) => {
            if (!row.action || row.action.trim() === '') return false;
            return !categoryId || row.categoryId === categoryId;
        });
    }, [data, categoryId]);

    const title = useMemo(() => {
        if (categoryName) {
            return `${LL.audit.title()} - ${categoryName}`;
        }
        const event = eventTitle ? ` - ${eventTitle}` : '';
        return `${LL.audit.title()}${event}`;
    }, [LL, eventTitle, categoryName]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ py: 1.25 }}>{title}</DialogTitle>
            <DialogContent dividers sx={{ py: 1 }}>
                {isLoading && (
                    <Stack sx={{ py: 4, alignItems: 'center' }}>
                        <CircularProgress />
                    </Stack>
                )}

                {!isLoading && isError && (
                    <Typography color="error" variant="body2">
                        {String(
                            (error as any)?.data?.message ??
                                (error as any)?.error ??
                                'Failed to load'
                        )}
                    </Typography>
                )}

                {!isLoading && !isError && (!validData || validData.length === 0) && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                        }}
                    >
                        {LL.audit.empty()}
                    </Typography>
                )}

                {!isLoading && !isError && validData && validData.length > 0 && (
                    <List dense disablePadding>
                        {validData.map((row: EventAuditEntry) => {
                            // Prefer meta.oldAssignedResourceName injected by the backend; otherwise resolve id locally
                            const oldAssigned = getOldAssignedFromMeta(row.meta);
                            const oldAssignedFromMetaName =
                                row.meta &&
                                typeof row.meta === 'object' &&
                                (row.meta as any).oldAssignedResourceName
                                    ? (row.meta as any).oldAssignedResourceName
                                    : null;

                            const oldAssignedLabel =
                                oldAssignedFromMetaName ??
                                (oldAssigned && resourceNameById.get(oldAssigned)
                                    ? resourceNameById.get(oldAssigned)
                                    : oldAssigned
                                      ? `#${oldAssigned}`
                                      : null);

                            const primary =
                                row.action === 'replace' && oldAssigned
                                    ? `${oldAssignedLabel ?? oldAssigned} → ${getAssignedLabel(row)}`
                                    : getAssignedLabel(row);

                            const actionAvatar = getActionAvatar(row.action);
                            if (!actionAvatar) return null; // Skip invalid entries

                            return (
                                <ListItem key={row.id} disableGutters sx={{ py: 0.5 }}>
                                    <Box sx={{ mr: 1, mt: 0.25 }}>{actionAvatar}</Box>
                                    <ListItemText
                                        primary={
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                sx={{
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: 0.5,
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 600, lineHeight: 1.2 }}
                                                >
                                                    {primary}
                                                </Typography>
                                                {!categoryId && row.categoryName && (
                                                    <Chip
                                                        label={row.categoryName}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.7rem',
                                                            bgcolor: 'action.selected',
                                                        }}
                                                    />
                                                )}
                                            </Stack>
                                        }
                                        secondary={
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: 'text.secondary',
                                                    lineHeight: 1.2,
                                                }}
                                            >
                                                {formatTime(row.createdAt)} · {LL.audit.by()}:{' '}
                                                {getActorLabel(row)}
                                            </Typography>
                                        }
                                        slotProps={{
                                            primary: { sx: { mt: 0 } },
                                            secondary: { sx: { mt: 0.25 } },
                                        }}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
            <DialogActions sx={{ py: 1 }}>
                <Button onClick={onClose}>{LL.common.close()}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EventAuditModal;
